<template>
  <Transition name="fade">
    <div
      v-if="isVisible && kycStatus && !isContactsModalVisible"
      class="kyc-modal"
    >
      <div class="kyc-modal__popup">
        <div class="kyc-modal__title">
          {{ title }}
        </div>
        <div class="kyc-modal__body">
          <div class="kyc-modal__icon-wrapper">
            <i :class="['kyc-modal__icon', iconClass, { 'kyc-modal__icon--success': isSuccessIcon }]" />
          </div>
          <div class="kyc-modal__msg">
            {{ message }}
          </div>
        </div>
        <div class="kyc-modal__footer">
          <button
            v-if="isPending"
            type="button"
            class="btn btn-outline-primary btn-sm"
            @click="handleRefresh"
          >
            <i class="fas fa-sync-alt" />
            {{ $t('filters.refresh') }}
          </button>
          <button
            v-if="isContactSupport"
            type="button"
            class="btn btn-outline-primary btn-sm"
            @click="handleContactUs"
          >
            <i class="fas fa-comments" />
            {{ $t('forms.btn.contactUs') }}
          </button>
          <button
            type="button"
            class="btn btn-outline-danger btn-sm"
            @click="handleLogout"
          >
            <i class="fas fa-sign-out" />
            {{ $t('topNavbar.logout') }}
          </button>
          <button
            v-if="showProceed"
            type="button"
            class="btn btn-primary btn-sm ui-flex-grow"
            :class="{ disabled: isProceedDisabled }"
            @click="handleProceed"
          >
            {{ $t('alerts.kycRequired.proceed') }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
  <Transition name="fade">
    <div
      v-if="isContactsModalVisible"
      class="kyc-modal kyc-modal--contacts"
    >
      <div class="kyc-modal__contacts-popup">
        <button
          type="button"
          class="close kyc-modal__contacts-close"
          @click="isContactsModalVisible = false"
        >
          <span class="close-icon">x</span>
        </button>
        <Contacts
          :redirect-on-send="false"
          @sent="isContactsModalVisible = false"
        />
      </div>
    </div>
  </Transition>
</template>

<script>
import { useIntercom } from '@homebaseai/vue3-intercom';
import CookiesProvider from '@/lib/cookieProvider';
import Contacts from '@/views/Dashboard/Contacts.vue';

export default {
  name: 'KycModal',
  components: {
    Contacts,
  },
  setup: () => ({ intercom: useIntercom() }),
  data() {
    return {
      isVisible: false,
      isContactsModalVisible: false,
      verificationUrl: '',
      loading: false,
      kycStatus: null,
    };
  },
  computed: {
    title() {
      if (this.isPending) {
        return this.$t('alerts.kycRequired.pendingTitle');
      }
      return this.$t('alerts.kycRequired.title');
    },
    message() {
      if (this.isResubmission) {
        return this.$t('alerts.kycRequired.retryMessage');
      }
      if (this.isCannotRequest) {
        return this.$t('alerts.kycRequired.cannotRequestMessage');
      }
      if (this.isFailed) {
        return this.$t('alerts.kycRequired.contactSupportMessage');
      }
      if (this.isPending) {
        return this.$t('alerts.kycRequired.pendingMessage');
      }

      return this.$t('alerts.kycRequired.message');
    },
    showProceed() {
      return this.isInit || this.isResubmission;
    },
    isInit() {
      return this.kycStatus === 'init';
    },
    isResubmission() {
      return this.kycStatus === 'resubmission';
    },
    isFailed() {
      return this.kycStatus === 'failed';
    },
    isCannotRequest() {
      return this.kycStatus === 'cannot_request';
    },
    isContactSupport() {
      return this.isFailed || this.isCannotRequest;
    },
    isPending() {
      return this.kycStatus === 'pending';
    },
    isSuccessIcon() {
      return this.isInit || this.isPending;
    },
    iconClass() {
      if (this.isPending) {
        return 'fas fa-ballot-check';
      }
      return 'fas fa-address-card';
    },
    isProceedDisabled() {
      return this.loading || !this.verificationUrl;
    },
  },
  created() {
    this.$eventBus.$on('kyc-required', this.handleKycRequired);
  },
  beforeUnmount() {
    this.$eventBus.$off('kyc-required', this.handleKycRequired);
  },
  methods: {
    handleKycRequired(code) {
      if (this.isVisible) return;

      this.isVisible = true;
      this.verificationUrl = '';

      if (code === 'AFFILIATE_KYC_IS_REQUIRED_ERROR') {
        this.fetchVerificationUrl().catch(() => {
          this.kycStatus = 'cannot_request';
        });
      } else {
        this.kycStatus = 'failed';
      }
    },
    fetchVerificationUrl() {
      this.loading = true;

      return this.$api.getKycVerificationUrl()
        .then((res) => {
          this.verificationUrl = res.payload?.verification_url || '';
          this.kycStatus = res.payload?.review_status || '';
        })
        .catch((err) => {
          if (err?.response?.data?.code === 'AFFILIATE_KYC_CANNOT_BE_REQUESTED_ERROR') {
            this.kycStatus = 'cannot_request';
            return null;
          }

          return Promise.reject(err);
        })
        .finally(() => this.loading = false);
    },
    handleProceed() {
      if (this.isProceedDisabled) return;

      this.fetchVerificationUrl()
        .then(() => {
          if (this.verificationUrl) {
            window.open(this.verificationUrl, '_blank');
          }
        })
        .catch((err) => {
          if (err?.response?.data?.code === 'AFFILIATE_KYC_IS_NOT_REQUESTED_ERROR') {
            window.location.reload();
          } else {
            this.kycStatus = 'cannot_request';
          }
        });
    },
    handleRefresh() {
      window.location.reload();
    },
    handleContactUs() {
      if (!this.$store.state.settings.intercom) {
        return;
      }

      if (!this.$store.state.settings.intercom.live_chat_available) {
        this.isContactsModalVisible = true;
        return;
      }

      if (!this.intercom.visible) {
        this.intercom.show();
        this.intercomUnreadCount = 0;
      } else {
        this.intercom.hide();
      }
    },
    handleLogout() {
      this.$store.dispatch('logout');
      CookiesProvider.remove('autologin_token');
      this.$router.push('/logout');
    },
  },
};
</script>

<style lang="scss">
.kyc-modal {
  position: fixed;
  inset: 0;
  z-index: 999999999;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;

  &.fade-enter-active,
  &.fade-leave-active {
    transition: all 0.4s;

    .kyc-modal__popup {
      transition: all 0.4s;
    }
  }

  &.fade-enter,
  &.fade-leave-to {
    opacity: 0;

    .kyc-modal__popup {
      transform: translate(-50%, -40%);
    }
  }

  &__popup {
    position: absolute;
    z-index: 999999;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    border-radius: 8px;
    background-color: #ffffff;
    display: flex;
    flex-direction: column;
    width: 380px;
    max-width: calc(100% - 32px);
    box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.09);
    padding: 22px 24px;
  }

  &__body {
    text-align: center;
    border-top: 1px solid #e7ecf1;
  }

  &__icon-wrapper {
    margin: 32px auto 16px;
  }

  &__icon {
    background-color: #ffffff;
    font-size: 48px;
    color: var(--color-text-red);
  }

  &__icon--success {
    color: var(--color-text-green);
  }

  &__title {
    font-size: 22px;
    font-weight: 600;
    margin-bottom: 18px;
  }

  &__msg {
    font-size: 14px;
    line-height: 1.71;
    margin: 0 16px 40px;
  }

  &__footer {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    border-top: 1px solid #e7ecf1;
    padding-top: 16px;
  }
}

.kyc-modal--contacts {
  .kyc-modal__contacts-popup {
    position: relative;
    width: 480px;
    max-width: calc(100% - 32px);
  }

  .kyc-modal__contacts-close {
    position: absolute;
    opacity: 1;
    z-index: 2;
    top: 20px;
    right: 24px;
  }

  .contacts__wrap {
    width: 100%!important;
    border-radius: 8px !important;
  }

  .modal__footer-btn {
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
</style>

